import * as React from "react";
import { BaseControl } from "VSS/Controls";
import { IMenuItemSpec } from "VSS/Controls/Menus";
import { TfsContext } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";
import { DiscussionManager } from "Presentation/Scripts/TFS/TFS.Discussion.OM";
import { ChangeList, GitDiffItem } from "VersionControl/Scripts/Stores/PullRequestReview/ChangeTransformer";
import { BranchesDiffChangeListControl } from "VersionControl/Scripts/Controls/PullRequestBranchesDiffChangeListControl";
import { RepositoryContext } from "VersionControl/Scripts/RepositoryContext";
import { NoChangesMessage, PullRequestFileContentIsTrimmed } from "VersionControl/Scripts/Resources/TFS.Resources.VersionControl";
import { ChangeExplorerGridDisplayMode, DiffViewerOrientation } from "VersionControl/Scripts/Generated/TFS.VersionControl.WebAccess.Contracts";
import { GitRepository } from "TFS/VersionControl/Contracts";

export interface IDiffSummaryViewerProps {
    tfsContext: TfsContext;
    repositoryContext: RepositoryContext;
    discussionManager: DiscussionManager;
    changeList: ChangeList;
    selectedDiffItem?: GitDiffItem;
    displayMode: ChangeExplorerGridDisplayMode;
    orientation: DiffViewerOrientation;
    isVisible: boolean;
    isFullScreen: boolean;
    enableShowMoreChangesLink: boolean;
    onOrientationChange(orientation: DiffViewerOrientation): void;
    additionalMenuItems?(): IMenuItemSpec[];
}

/**
 * hybrid renderer for jquery change list component
 */
export class DiffSummaryViewer extends React.Component<IDiffSummaryViewerProps, {}> {
    private _diffSummaryViewerControl: BranchesDiffChangeListControl

    public render(): JSX.Element {
        return <div className="vc-pullrequest-details-files"></div>;
    }

    public componentDidMount(): void {
        const repository = (this.props.repositoryContext.getRepository() as GitRepository);

        this._diffSummaryViewerControl =
            BaseControl.createIn(BranchesDiffChangeListControl, $(".vc-pullrequest-details-files"), {
                tfsContext: this.props.tfsContext,
                hideArtifactLevelDiscussions: true,
                supportCommentStatus: true,
                headerTitle: repository.name,
                collapseIfNoChange: true,
                keepFilePanelExpandState: true,
                changesModelChangesChangedCallback: () => { },
                orientationChangeCallback: this.props.onOrientationChange,
                additionalMenuItems: this.props.additionalMenuItems,
                noChangesMessage: NoChangesMessage,
                contentTruncatedMessage: PullRequestFileContentIsTrimmed,
                enableShowMoreChangesLink: this.props.enableShowMoreChangesLink,
                prDetailsSummary: true
        }) as BranchesDiffChangeListControl;
        this._diffSummaryViewerControl.setAllowHideComments(false);

        // do an initial refresh in case it is needed
        this.componentDidUpdate({} as IDiffSummaryViewerProps);
    }

    public componentWillUnmount(): void {
        if (this._diffSummaryViewerControl) {
            this._diffSummaryViewerControl.dispose();
            this._diffSummaryViewerControl = null;
        }
    }

    public componentDidUpdate(prevProps?: IDiffSummaryViewerProps): void {
        if (this._diffSummaryViewerControl && 
            this.props.changeList && 
            this.props.selectedDiffItem && 
            this.props.isVisible) {

            // reload data in the control
            this._diffSummaryViewerControl.setDiscussionManager(this.props.discussionManager);

            // reset the model if the changelists are different or if the display mode has changed
            const shouldResetModel: boolean =
                this.props.changeList.legacyChangeList() !== this._diffSummaryViewerControl.getCurrentChangeModel() ||
                this.props.displayMode !== this._diffSummaryViewerControl.getCurrentDisplayMode();

            if (!shouldResetModel) {
                // if the changelist is the same, then only reset the filter
                // (this avoids an XHR)
                this._diffSummaryViewerControl.setFilter({
                    path: this.props.selectedDiffItem.mpath,
                    recursive: true,
                });
            } else {
                // if the changelist is different we need to reset the whole model
                this._diffSummaryViewerControl.setModel(
                    this.props.repositoryContext,
                    this.props.changeList.legacyChangeList(),
                    this.props.selectedDiffItem.oversion,
                    this.props.selectedDiffItem.mversion,
                    {
                        path: this.props.selectedDiffItem.mpath,
                        recursive: true,
                    },
                    this.props.orientation,
                    this.props.displayMode);
            }

            this._diffSummaryViewerControl.showElement();
            this._diffSummaryViewerControl.setActiveState(true, true);

            // if this is a component update (and not a mount) and fullscreen has changed, update the menu bar to update
            // the fullscreen button label and icon
            if (prevProps.isFullScreen !== undefined && prevProps.isFullScreen !== this.props.isFullScreen) {
                this._diffSummaryViewerControl.updateAdditionalMenuItems(this.props.additionalMenuItems());
            }
        }
    }

    public shouldComponentUpdate(nextProps: IDiffSummaryViewerProps, nextState: {}): boolean {
        if (!nextProps.changeList) {
            // there is a chance the changelist will be null during switches between iterations
            // in order to avoid blowing up the jquery control we will just not update this cycle
            return false; 
        }

        return (this.props.discussionManager !== nextProps.discussionManager
            || this.props.selectedDiffItem !== nextProps.selectedDiffItem
            || this.props.changeList !== nextProps.changeList
            || this.props.displayMode !== nextProps.displayMode
            || this.props.isVisible !== nextProps.isVisible
            || this.props.isFullScreen !== nextProps.isFullScreen);
    }
}
